import React, {useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {createAddress, getAddressesSuggestList, getAddressInfoFromString} from 'api/geo';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {AsyncSelect} from 'components/ui-kit/Select';
import {factoryDetailsState$} from 'models/profile/factories/factoryDetails';
import {
    factoryOperationsState$,
    isEditModalOpenChanged,
    updateFactory,
} from 'models/profile/factories/factoryOperations';
import {Address} from 'types/geo';
import {CreateFactory, EditFactoryRequestData} from 'types/profile';
import {formatAddressInfoToRequest} from 'utils/address';

import {factoryEditSchema} from '../helpers';

import {FactoryLines} from './FactoryLines';
import {
    Actions,
    CancelButton,
    CancelCloseButton,
    CancelModal,
    CustomModal,
    FactoryWrapper,
    Field,
} from './styled';

export const FactoryEdit = () => {
    const {t, i18n} = useTranslation();
    const {showEdit, editPending} = useStore(factoryOperationsState$);
    const [cancelModal, setCancelModal] = useState(false);

    const {factory} = useStore(factoryDetailsState$);

    const formData = useForm({mode: 'onChange', resolver: yupResolver(factoryEditSchema)});

    const addressOptions = async (partialAddress: string) => {
        try {
            if (!partialAddress) return [{value: '', label: ''}];
            const lang =
                (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) || 'ru';
            const payload: Address = {
                address: partialAddress,
                lang,
            };

            const values = (await getAddressesSuggestList(payload)).data;
            return values.map((value) => ({value, label: value}));
        } catch (error) {
            console.error(error);
            return [{value: '', label: ''}];
        }
    };

    const getAddressId = async (address?: string) => {
        if (!address) return;
        try {
            const addressPayload = (partialAddress: string): Address => {
                const lang =
                    (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) ||
                    'ru';
                return {
                    address: partialAddress,
                    lang,
                };
            };

            const payload = addressPayload(address);
            return await getAddressInfoFromString(payload)
                .then(formatAddressInfoToRequest)
                .then(createAddress);
        } catch (error) {
            console.error(error);
        }
    };

    const onSubmit = async (data: CreateFactory) => {
        const addressId = await getAddressId(data.address);
        const result: EditFactoryRequestData = {
            factoryUpdate: {
                name: data.name,
                address: data.address || '',
                addressId,
                country: factory?.country || null,
                countryCode: factory?.countryCode || null,
            },
            factoryLineOperations:
                data.factoryLines?.map((line) => {
                    const {customId, storedId, ...rest} = line;
                    return {...rest, ...(storedId && {id: storedId}), productName: ' '};
                }) || [],
        };
        updateFactory({
            id: factory!.id,
            data: result,
        });
    };

    const close = () => {
        setCancelModal(true);
    };

    const closeConfirmed = () => {
        setCancelModal(false);
        formData.reset();
        isEditModalOpenChanged(false);
    };

    return (
        <CustomModal
            modalWidth={1}
            title={t('pages.profile.factoryEdit.title')}
            show={showEdit}
            onClose={close}
        >
            <CancelModal show={cancelModal} hideCloseButton onClose={() => {}}>
                <p>{t('pages.profile.createFactory.cancelText')}</p>
                <Actions>
                    <CancelCloseButton onClick={closeConfirmed}>
                        {t('pages.profile.createFactory.cancelClose')}
                    </CancelCloseButton>
                    <CancelButton
                        onClick={() => setCancelModal(false)}
                        variant={ButtonVariant.Outline}
                    >
                        {t('pages.profile.createFactory.cancel')}
                    </CancelButton>
                </Actions>
            </CancelModal>
            <form onSubmit={formData.handleSubmit(onSubmit)}>
                <FactoryWrapper>
                    <Field>
                        <Controller
                            name={`name`}
                            defaultValue={factory?.name}
                            control={formData.control}
                            render={(props) => (
                                <Input
                                    {...props}
                                    placeholder={t('pages.profile.createFactory.name')}
                                />
                            )}
                        />
                    </Field>
                    <Field>
                        <Controller
                            name={`address`}
                            control={formData.control}
                            defaultValue={factory?.address}
                            render={({onChange, value, name}) => (
                                <AsyncSelect
                                    name={name}
                                    placeholder={t('pages.profile.createFactory.address')}
                                    loadOptions={addressOptions}
                                    isClearable
                                    onChange={(v?: {value: string; label: string}) => {
                                        onChange(v?.value || '');
                                    }}
                                    value={value ? {value, label: value} : null}
                                />
                            )}
                        />
                    </Field>
                    <FactoryLines
                        formData={formData}
                        initialFactoryLines={factory?.factoryLines ?? []}
                    />
                </FactoryWrapper>
                <Actions>
                    <Button isLoading={editPending} disabled={!formData.formState.isValid}>
                        {t('pages.profile.createFactory.save')}
                    </Button>
                    <Button type="button" variant={ButtonVariant.Outline} onClick={close}>
                        {t('pages.profile.createFactory.cancel')}
                    </Button>
                </Actions>
            </form>
        </CustomModal>
    );
};
